import { _js } from '@ifixit/utils/localize';
import { onDomReady } from 'Shared/utils/dom-ready';
import { callAjaxIO } from 'Shared/ajax_io';
import { delegate } from 'Shared/utils/delegate';

let resetModalAbortController = null;

function getNextMatching(el, selector) {
   let next = el.nextElementSibling;
   while (next && !next.matches(selector)) {
      next = next.nextElementSibling;
   }
   return next;
}

function getPreviousMatching(el, selector) {
   let prev = el.previousElementSibling;
   while (prev && !prev.matches(selector)) {
      prev = prev.previousElementSibling;
   }
   return prev;
}

const ModerationPage = {
   voteFilter: 'all',
   selected: null,
   shortcutsEnabled: true,
   UP_VOTE: 1,
   DOWN_VOTE: -1,
   toggleContext: function (el) {
      let entry = el.closest('.vote-entry');
      let contextArea = entry.querySelector(':scope > .context-area');
      let detailsIcon = el.querySelector(':scope > .details-icon');
      let link = el.querySelector(':scope > .vote-details');

      let expandText = _js('Show details');
      let collapseText = _js('Hide details');

      if (entry.classList.contains('context-shown')) {
         detailsIcon.innerHTML = '+';
         detailsIcon.classList.remove('hide-icon');
         link.textContent = expandText;
         entry.classList.remove('context-shown');
         contextArea.classList.add('hidden');
      } else {
         detailsIcon.innerHTML = '&ndash;';
         detailsIcon.classList.add('hide-icon');
         link.textContent = collapseText;
         entry.classList.add('context-shown');
         contextArea.classList.remove('hidden');
      }
   },

   setupContextLinks: function () {
      document.querySelectorAll('.vote-entry').forEach(entry => {
         entry.querySelectorAll('.details-container').forEach(container => {
            container.addEventListener('click', function (ev) {
               ev.preventDefault();
               ev.stopPropagation();
               ModerationPage.toggleContext(this);
            });
         });
      });
   },

   select: function (entry) {
      if (this.selected) {
         this.selected.classList.remove('selected');
      }
      entry.classList.add('selected');
      entry.scrollIntoView({
         behavior: 'smooth',
         block: 'center',
      });
      this.selected = entry;
   },

   openKeyboardModal: function () {
      void import('Shared/modal').then(({ Modal }) => {
         Modal.open({
            type: 'element',
            element: document.querySelector('#shortcuts-modal'),
         });
      });
   },

   hasSelected: function () {
      return ModerationPage.selected;
   },

   setupKeyboardShortcuts: function () {
      window.addEventListener('keydown', ev => {
         // MooTools lowercased its key names, so these fire with Caps Lock or
         // Shift held; ev.key is case-sensitive, so normalize letters.
         const key = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
         switch (key) {
            case '?': {
               ModerationPage.keyAction('openModal', ev);

               break;
            }
            case 's':
            case 'ArrowDown': {
               ModerationPage.keyAction('moveDown', ev);

               break;
            }
            case 'w':
            case 'ArrowUp': {
               ModerationPage.keyAction('moveUp', ev);

               break;
            }
            case 'e': {
               ModerationPage.keyAction('expand', ev);

               break;
            }
            case 'p': {
               ModerationPage.keyAction('passVote', ev);

               break;
            }
            case 'f': {
               ModerationPage.keyAction('failVote', ev);

               break;
            }
            // No default
         }
      });
   },

   keyAction: function (action, ev) {
      if (document.querySelectorAll('.modalOpen').length || !ModerationPage.shortcutsEnabled) {
         return;
      }

      let selected = ModerationPage.selected;
      ev.preventDefault();
      ev.stopPropagation();

      if (action == 'openModal') {
         ModerationPage.openKeyboardModal();
      }

      if (!selected && !(ev.metaKey || ev.ctrlKey || ev.shiftKey)) {
         let entries = [...document.querySelectorAll('.vote-entry')].filter(
            entry => !entry.classList.contains('voted')
         );

         if (entries.length > 0) {
            ModerationPage.select(entries[0]);
         }
      } else if (selected) {
         let voteid = selected.dataset.id;
         let next = getNextMatching(selected, '.vote-entry');

         if (action == 'moveDown') {
            if (next) {
               ModerationPage.select(next);
            }
         } else if (action == 'moveUp') {
            let prev = getPreviousMatching(selected, '.vote-entry');
            if (prev) {
               ModerationPage.select(prev);
            }
         } else if (action == 'expand') {
            let detailsDiv = selected.querySelector(':scope > .details-container');
            ModerationPage.toggleContext(detailsDiv);
         } else if (action == 'passVote') {
            if (selected.classList.contains('pending')) {
               ModerationPage.vote(selected, voteid, ModerationPage.UP_VOTE);

               if (next) {
                  ModerationPage.select(next);
               }
            }
         } else if (action == 'failVote' && selected.classList.contains('pending')) {
            ModerationPage.vote(selected, voteid, ModerationPage.DOWN_VOTE);
            if (next) {
               ModerationPage.select(next);
            }
         }
      }
   },

   setupVoteButtons: function () {
      document.querySelectorAll('.pass-fail-buttons').forEach(buttons => {
         let voteid = buttons.closest('.vote-entry').dataset.id;
         if (buttons.dataset.userVote == ModerationPage.UP_VOTE) {
            buttons.querySelector(':scope > .js-pass-vote').addEventListener('click', ev => {
               ev.preventDefault();
               ev.stopPropagation();
               ModerationPage.resetVoteModal(voteid);
            });
         } else if (buttons.dataset.userVote == ModerationPage.DOWN_VOTE) {
            buttons.querySelector(':scope > .js-fail-vote').addEventListener('click', ev => {
               ev.preventDefault();
               ev.stopPropagation();
               ModerationPage.resetVoteModal(voteid);
            });
         } else {
            let passButton = buttons.querySelector(':scope > .js-pass-vote');
            if (passButton) {
               passButton.addEventListener('click', ev => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  ModerationPage.vote(passButton, voteid, ModerationPage.UP_VOTE);
               });
            }

            let failButton = buttons.querySelector(':scope > .js-fail-vote');
            if (failButton) {
               failButton.addEventListener('click', ev => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  ModerationPage.vote(failButton, voteid, ModerationPage.DOWN_VOTE);
               });
            }
         }
      });
   },

   refreshContent: function (voteStatus) {
      let filter = ModerationPage.voteFilter;
      callAjaxIO('refreshVotes', voteStatus, filter)
         .then(data => {
            if (data.error != null) {
               return alert(data.error);
            }

            document.querySelector('#moderation').innerHTML = data.html;
            ModerationPage.setupContextLinks();
            ModerationPage.setupVoteButtons();

            if (ModerationPage.selected) {
               document.querySelectorAll('.vote-entry').forEach(entry => {
                  if (entry.dataset.id == ModerationPage.selected.dataset.id) {
                     ModerationPage.select(entry);
                  }
               });
            }
         })
         .catch(error => console.error(error));
   },

   vote: function (button, voteid, score) {
      const toggle = document.querySelector('#superPowersToggle');
      const adminForced = toggle && toggle.checked;
      button.classList.add('vote-button-disabled');

      const voteRequest = callAjaxIO('vote', voteid, score, adminForced);
      voteRequest
         .then(() => {
            ModerationPage.refreshContent('pending');
         })
         .catch(error => console.error(error))
         .finally(() => button.classList.remove('vote-button-disabled'));

      LoadingIndicator.withPromise(voteRequest, {
         pendingMessage: _js('Processing'),
         successMessage: _js('Vote Recorded'),
         failureMessage: _js('Error Recording Vote'),
      });
   },

   resetVoteModal: function (voteid) {
      void import('Shared/modal').then(({ Modal }) => {
         let modal = document.querySelector('#reset-modal');
         resetModalAbortController?.abort();
         resetModalAbortController = new AbortController();
         document.querySelectorAll('#reset-modal .reset-confirm').forEach(confirm => {
            confirm.addEventListener(
               'click',
               ev => {
                  Modal.close();
                  ModerationPage.cancelVote(voteid);
               },
               { signal: resetModalAbortController.signal }
            );
         });

         Modal.open({
            type: 'element',
            element: modal,
         });
      });
   },

   cancelVote: function (voteid) {
      callAjaxIO('cancelVote', voteid)
         .then(() => {
            ModerationPage.refreshContent('pending');
         })
         .catch(error => console.error(error));
   },
};

onDomReady(() => {
   ModerationPage.setupContextLinks();
   ModerationPage.setupKeyboardShortcuts();
   ModerationPage.setupVoteButtons();

   document.querySelectorAll('input').forEach(input => {
      input.addEventListener('focus', () => {
         ModerationPage.shortcutsEnabled = false;
      });

      input.addEventListener('blur', () => {
         ModerationPage.shortcutsEnabled = true;
      });
   });

   document.querySelectorAll('#vote-filters a').forEach(link => {
      link.addEventListener('click', ev => {
         ev.preventDefault();
         ev.stopPropagation();
         ModerationPage.voteFilter = link.dataset.votefilter;
         document
            .querySelectorAll('#vote-filters a')
            .forEach(el => el.classList.remove('filter-option-active'));
         link.classList.add('filter-option-active');
         ModerationPage.refreshContent('pending');
      });

      link.addEventListener('mouseenter', ev => {
         link.classList.add('activeButton');
      });

      link.addEventListener('mouseleave', ev => {
         link.classList.remove('activeButton');
      });
   });

   document.querySelectorAll('.js-toggle-selected').forEach(toggle => {
      toggle.addEventListener('click', function () {
         let target = this.parentElement.querySelector('.js-toggle-target');
         target.classList.toggle('toggle-open');
      });
   });

   delegate(document.querySelector('#content'), 'click', '.js-toggle-target', () => {
      document
         .querySelectorAll('.js-toggle-target')
         .forEach(t => t.classList.remove('toggle-open'));
      document.querySelectorAll('.js-toggle-selected').forEach(t => t.classList.remove('selected'));
   });

   if (App.totalVotes === 1) {
      document.querySelector('.vote-details').click();
   }
});
